import { Check, Ellipsis } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useAppStore } from '../../app/store';
import { styleName } from '../../data/styles';
import type { Project } from '../../data/types';
import { STEP_COUNT, doneCount } from '../../data/wizardSteps';
import { BottomSheet } from '../../ui/BottomSheet';
import { Button, IconButton } from '../../ui/Button';
import { FilterChip } from '../../ui/FilterChip';
import { Icon } from '../../ui/Icon';
import { Row, RowList } from '../../ui/Row';
import { EmptyState, Progress } from '../../ui/States';
import screen from '../screen.module.css';
import { useProjectImport } from './ImportProject';
import { NewProjectSheet } from './NewProjectSheet';
import { useProjects } from './projectsStore';
import styles from './Beat.module.css';

/** The active project first, then the rest by last change (SPEC F7). */
function ordered(projects: Project[], activeId: string | undefined): Project[] {
  return [...projects].sort((a, b) => (a.id === activeId ? -1 : b.id === activeId ? 1 : b.updatedAt - a.updatedAt));
}

/** Beat › Projects (SPEC F7): active, then recent; archived behind a filter. Mark finished, Duplicate, Archive and Delete with Undo. */
export function ProjectsScreen() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { projects, create, open, duplicate, archive, remove, restore, rename } = useProjects();
  const activeId = useAppStore((s) => s.settings.activeProjectId);
  const setActive = useAppStore((s) => s.setActiveProject);
  const showToast = useAppStore((s) => s.showToast);
  const [archivedView, setArchivedView] = useState(false);
  const [more, setMore] = useState<Project>();
  const [hasSamples, setHasSamples] = useState<boolean>();
  const newOpen = params.get('new') === '1';
  const importer = useProjectImport();

  useEffect(() => { void useProjects.getState().load(); }, []);
  useEffect(() => {
    if (!newOpen) return;
    void import('../../db/repos/samples').then((m) => m.countSamples()).then((n) => setHasSamples(n > 0), () => setHasSamples(false));
  }, [newOpen]);

  const closeNew = useCallback(() => setParams({}, { replace: true }), [setParams]);
  const closeMore = useCallback(() => setMore(undefined), []);

  const shown = useMemo(
    () => (projects ? ordered(projects.filter((p) => p.archived === archivedView), activeId) : undefined),
    [projects, archivedView, activeId],
  );
  const archivedCount = projects?.filter((p) => p.archived).length ?? 0;

  const start = async () => {
    const p = await create();
    if (!p) return; // the save failed: the toast says why, and the sheet stays open to try again
    closeNew();
    navigate(`/beat/${p.id}`);
  };

  const undoable = (message: string, run: () => Promise<void>) => {
    showToast({ message, action: { label: 'Undo', run: () => void run() } });
  };

  const doDuplicate = async (p: Project) => {
    closeMore();
    const copy = await duplicate(p.id);
    if (copy) undoable(`Duplicated as ${copy.name}`, () => remove(copy.id));
  };

  const doArchive = async (p: Project, archived: boolean) => {
    closeMore();
    await archive(p.id, archived);
    undoable(archived ? `${p.name} archived` : `${p.name} restored from the archive`, () => archive(p.id, !archived));
  };

  // Mark finished / Not finished (SPEC F10): no session outcome, the check-out sheet is not touched. Undo puts the old value back.
  const doFinished = async (p: Project, finished: boolean) => {
    closeMore();
    const { markFinished, setFinishedAt } = useProjects.getState();
    const prev = finished ? await markFinished(p.id) : await setFinishedAt(p.id, null);
    if (prev === undefined) return;
    undoable(finished ? 'Marked as finished' : 'Marked as not finished', async () => { await setFinishedAt(p.id, prev); });
  };

  const doDelete = async (p: Project) => {
    closeMore();
    const wasActive = p.id === activeId;
    await remove(p.id);
    undoable(`${p.name} deleted`, async () => {
      await restore(p.id);
      if (wasActive) setActive(p.id);
    });
  };

  if (!shown || !projects) return <div className={screen.screen}><h1 className="t-title">Projects</h1><Progress active label="Loading projects" /></div>;

  return (
    <div className={screen.screen}>
      <h1 className="t-title">Projects</h1>

      {archivedCount > 0 && (
        <div className={styles.filters} role="group" aria-label="Show">
          <FilterChip selected={!archivedView} onClick={() => setArchivedView(false)}>Active</FilterChip>
          <FilterChip selected={archivedView} onClick={() => setArchivedView(true)}>Archived</FilterChip>
        </div>
      )}

      {importer.panel}

      {shown.length === 0 && (
        <EmptyState
          message={archivedView ? 'Nothing archived. Archive a project to keep it out of the way.' : 'No projects yet. Start one to follow a beat from the first mark to the bounce.'}
          action={archivedView ? undefined : { label: 'New project', onClick: () => setParams({ new: '1' }, { replace: true }) }}
        />
      )}
      {shown.length === 0 && !archivedView && (
        <div className={styles.bottom} data-bottom-actions>
          <Button full disabled={importer.busy} onClick={importer.pick}>Import project</Button>
        </div>
      )}
      {shown.length > 0 && (
        <div style={{ marginTop: 'var(--s5)' }}>
          <RowList label={archivedView ? 'Archived projects' : 'Projects'}>
            {shown.map((p) => (
              <li key={p.id}>
                <Row
                  title={p.name}
                  meta={`${styleName(p.styleId)}, ${doneCount(p.checks)} of ${STEP_COUNT} steps done`}
                  trailing={p.id === activeId ? <Icon icon={Check} /> : undefined}
                  chevron
                  onClick={() => { if (!p.archived) open(p.id); navigate(`/beat/${p.id}`); }}
                  action={<IconButton icon={Ellipsis} aria-label={`More for ${p.name}`} variant="tertiary" onClick={() => setMore(p)} />}
                />
              </li>
            ))}
          </RowList>
        </div>
      )}

      {!(shown.length === 0 && !archivedView) && (
        <div className={styles.bottom} data-bottom-actions>
          <div className={styles.bottomRow}>
            <Button disabled={importer.busy} onClick={importer.pick}>Import project</Button>
            <Button variant="primary" onClick={() => setParams({ new: '1' }, { replace: true })}>New project</Button>
          </div>
        </div>
      )}

      {importer.field}
      <NewProjectSheet open={newOpen} hasSamples={hasSamples} onStart={start} onClose={closeNew} />

      <BottomSheet open={Boolean(more)} title={more?.name ?? 'Project'} onClose={closeMore}>
        {more && (
          <div className={styles.sheetBody}>
            <label className={styles.field} htmlFor="project-name">
              <span className="t-label">Name</span>
              <input
                id="project-name"
                className={styles.input}
                defaultValue={more.name}
                maxLength={60}
                onBlur={(e) => { if (e.target.value.trim() && e.target.value.trim() !== more.name) void rename(more.id, e.target.value); }}
                onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
              />
            </label>
            <RowList label="Project actions">
              <li>
                <Row
                  title={more.finishedAt === undefined ? 'Mark finished' : 'Not finished'}
                  meta={more.finishedAt === undefined ? 'The beat is bounced on the MPC' : 'Clears the finished date'}
                  onClick={() => void doFinished(more, more.finishedAt === undefined)}
                />
              </li>
              <li><Row title="Duplicate" meta="A copy with its own kit and groove" onClick={() => void doDuplicate(more)} /></li>
              <li>
                <Row
                  title={more.archived ? 'Unarchive' : 'Archive'}
                  meta={more.archived ? 'Back to the active list' : 'Keeps everything, out of the way'}
                  onClick={() => void doArchive(more, !more.archived)}
                />
              </li>
              <li><Row title="Delete" meta="Undo stays available for 24 hours" onClick={() => void doDelete(more)} /></li>
            </RowList>
          </div>
        )}
      </BottomSheet>
    </div>
  );
}
